import * as Sentry from '@sentry/react';
import { ScrollToTop } from '@/chassis/ui/components';
import ReactDOM from 'react-dom/client';
import React from 'react';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from '@/chassis/core/common-state/store/store';
import { AuthProvider } from '@/chassis/core/auth/AuthProvider';
import { buildGuardedRoutes } from '@/chassis/core/auth/route-guards';
import { resolveRoutePermission } from '@/access-control.config';
import { appRouteConfigs } from '@/main-routes';
import { LanguageProvider } from '@/chassis/ui/i18n/LanguageProvider';
import { RepresentationSelectionProvider } from '@/domains/pages/0.1-user-pages/UserShared/representation-selection';
import TrialExperience from '@/domains/pages/5.5-subscription-module-trial/TrialExperience';
import '@/chassis/ui/assets/css/app.css';

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: Boolean(import.meta.env.VITE_SENTRY_DSN),
    environment: import.meta.env.MODE,
    enableLogs: true,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
});

// ── Cleanup: remove atributos stale do HeadlessUI que sobrevivem a navegacao ──
// Quando o sessionExpired handler faz window.location.replace(), o React tree e
// destruido sem rodar cleanup effects. O HeadlessUI Dialog deixa inert=true e
// aria-hidden="true" no #root, bloqueando toda interacao na proxima pagina.
(function cleanupStaleHeadlessUI() {
    const root = document.getElementById('root');
    if (root) {
        root.removeAttribute('aria-hidden');
        if ('inert' in root) {
            (root as any).inert = false;
        }
        root.removeAttribute('inert');
    }
    document.querySelectorAll('[data-headlessui-portal]').forEach((el) => el.remove());
    document.documentElement.style.removeProperty('overflow');
})();

function AppRouter() {
    const routes = React.useMemo(
        () =>
            buildGuardedRoutes(appRouteConfigs, {
                resolvePermission: resolveRoutePermission,
            }),
        []
    );
    return useRoutes(routes);
}

function SuspenseFallback() {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
            Carregando...
        </div>
    );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <BrowserRouter>
        <Provider store={store}>
            <AuthProvider>
                <RepresentationSelectionProvider>
                    <LanguageProvider>
                        <React.Suspense fallback={<SuspenseFallback />}>
                            <AppRouter />
                        </React.Suspense>
                        {/* Teste grátis (5.5): botão da Celeri com os dias que faltam; nada para quem não está em teste. */}
                        <TrialExperience />
                    </LanguageProvider>
                </RepresentationSelectionProvider>
            </AuthProvider>
        </Provider>
        <ScrollToTop />
    </BrowserRouter>
);
